/**
 * AiChatPage — dedicated full-page AI chat at /ai-chat.
 *
 * Three-column layout: ThreadListSidebar + AiChat + WorkspacePanel (collapsible).
 * The AiChatRuntime wraps only the chat area so thread switching
 * remounts just the chat, not the sidebar or workspace.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Bug, FolderOpen, Pencil, Check, X } from 'lucide-react';
import { AiChat } from './AiChat';
import { AiChatRuntime } from './AiChatRuntime';
import { ThreadListSidebar } from './ThreadListSidebar';
import { WorkspacePanel } from './WorkspacePanel';
import { SandboxEditorPanel } from './SandboxEditorPanel';
import { DebugPanel, DebugLogProvider } from './DebugPanel';
import { useSectionContext, useThreadState } from './AiChatProvider';
import { useAuth } from '@/contexts/AuthContext';
import { threadApi } from './adapters/firestore-thread-adapter';

const WS_OPEN_KEY = 'ai-chat-workspace-open';
const DBG_OPEN_KEY = 'ai-chat-debug-open';

/** Open unless it was closed last time. */
function readInitialWorkspace(): boolean {
  try { return localStorage.getItem(WS_OPEN_KEY) !== 'false'; } catch { return true; }
}

export function AiChatPage() {
  const { setSectionContext } = useSectionContext();
  const { currentThreadId, setCurrentThreadIdDirect, refreshThreads, switchThread, openThreadFromRoute, threadVersion } = useThreadState();
  const { user } = useAuth();
  const { threadId: routeThreadId } = useParams();
  const navigate = useNavigate();
  const [workspaceOpen, setWorkspaceOpen] = useState(readInitialWorkspace);
  const [creatingThread, setCreatingThread] = useState(false);
  const threadCreateRef = useRef<Promise<string> | null>(null);

  // ── Thread title (header) ────────────────────────────────────────────────
  const [threadTitle, setThreadTitle] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const skipTitleBlurRef = useRef(false);

  const [debugOpen, setDebugOpen] = useState(() => {
    try { return localStorage.getItem(DBG_OPEN_KEY) === 'true'; } catch { return false; }
  });

  const getToken = useMemo(() => {
    return async (): Promise<string | null> => {
      if (user && 'getIdToken' in user && typeof user.getIdToken === 'function') {
        try { return await user.getIdToken(); } catch { return null; }
      }
      return null;
    };
  }, [user]);

  useEffect(() => {
    setSectionContext(null);
  }, [setSectionContext]);

  // ── URL ↔ thread reconciliation ──────────────────────────────────────────
  // The URL is the source of truth. `/ai-chat/<id>` adopts that thread (deep
  // link, browser back/forward); bare `/ai-chat` reflects the active thread
  // into the URL when one exists (e.g. resuming the last thread on landing),
  // otherwise it's a fresh chat.
  useEffect(() => {
    if (routeThreadId) {
      if (routeThreadId !== currentThreadId) openThreadFromRoute(routeThreadId);
    } else if (currentThreadId) {
      navigate(`/ai-chat/${currentThreadId}`, { replace: true });
    }
  }, [routeThreadId, currentThreadId, openThreadFromRoute, navigate]);

  // Keep the header title in sync with the active thread. Refetches on thread
  // switch and whenever the thread list is refreshed (rename / auto-title).
  useEffect(() => {
    if (!currentThreadId) {
      setThreadTitle(null);
      setEditingTitle(false);
      return;
    }
    let cancelled = false;
    threadApi
      .get(currentThreadId)
      .then((t) => { if (!cancelled) setThreadTitle(t?.title ?? null); })
      .catch(() => { if (!cancelled) setThreadTitle(null); });
    return () => { cancelled = true; };
  }, [currentThreadId, threadVersion]);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.select();
  }, [editingTitle]);

  const startEditTitle = useCallback(() => {
    setTitleDraft(threadTitle ?? '');
    setEditingTitle(true);
  }, [threadTitle]);

  const cancelEditTitle = useCallback(() => {
    skipTitleBlurRef.current = true; // discard the blur-triggered save
    setEditingTitle(false);
  }, []);

  const saveTitle = useCallback(async () => {
    if (skipTitleBlurRef.current) {
      skipTitleBlurRef.current = false;
      return;
    }
    const trimmed = titleDraft.trim();
    if (!currentThreadId || !trimmed || trimmed === threadTitle) {
      setEditingTitle(false);
      return;
    }
    const prev = threadTitle;
    setThreadTitle(trimmed); // optimistic
    setEditingTitle(false);
    try {
      await threadApi.update(currentThreadId, { title: trimmed });
      refreshThreads(); // update the left rail
    } catch (err) {
      console.warn('[AiChat] Rename failed:', err);
      setThreadTitle(prev);
    }
  }, [currentThreadId, titleDraft, threadTitle, refreshThreads]);

  // Eagerly create a thread when workspace is opened and no thread exists
  useEffect(() => {
    if (!workspaceOpen || currentThreadId || creatingThread || threadCreateRef.current) return;

    setCreatingThread(true);
    threadCreateRef.current = threadApi
      .create({ title: 'New Chat', messages: [] })
      .then((created) => {
        setCurrentThreadIdDirect(created.id);
        refreshThreads();
        return created.id;
      })
      .catch((err) => {
        console.warn('[AiChat] Failed to create thread for workspace:', err);
        throw err;
      })
      .finally(() => {
        setCreatingThread(false);
        threadCreateRef.current = null;
      });
  }, [workspaceOpen, currentThreadId, creatingThread, setCurrentThreadIdDirect, refreshThreads]);

  const toggleWorkspace = useCallback(() => {
    setWorkspaceOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(WS_OPEN_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // The workspace panel is the sandbox's VS Code. Only a deployment without a
  // sandbox backend has nothing to run it in, and gets the plain file list.
  const [editorUnavailable, setEditorUnavailable] = useState(false);
  const markEditorUnavailable = useCallback(() => setEditorUnavailable(true), []);
  const showEditor = !!currentThreadId && !editorUnavailable;
  // An opened file floats over the chat column rather than squeezing it.
  const chatColumnRef = useRef<HTMLDivElement>(null);

  const closeWorkspace = useCallback(() => {
    setWorkspaceOpen(false);
    try { localStorage.setItem(WS_OPEN_KEY, 'false'); } catch {}
  }, []);

  const toggleDebug = useCallback(() => {
    setDebugOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(DBG_OPEN_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const closeDebug = useCallback(() => {
    setDebugOpen(false);
    try { localStorage.setItem(DBG_OPEN_KEY, 'false'); } catch {}
  }, []);

  const [newThreadBusy, setNewThreadBusy] = useState(false);
  const handleNewThreadWithWorkspace = useCallback(async () => {
    if (!currentThreadId || newThreadBusy) return;
    setNewThreadBusy(true);
    try {
      // Returns as soon as the empty thread exists; the workspace copies in the
      // background and the panel shows a "copying" state until it lands.
      const created = await threadApi.newWithWorkspace(currentThreadId);
      refreshThreads();
      switchThread(created.id);
    } catch (err) {
      console.warn('[AiChat] Failed to start new chat with workspace:', err);
    } finally {
      setNewThreadBusy(false);
    }
  }, [currentThreadId, newThreadBusy, refreshThreads, switchThread]);

  const handleUseInChat = useCallback(
    (files: Array<{ name: string; path: string; url: string }>) => {
      const textarea = document.querySelector<HTMLTextAreaElement>(
        '[role="textbox"], textarea[placeholder]',
      );
      if (!textarea) return;

      const tag = files.map((f) => `[workspace: ${f.path}]`).join(' ');
      const start = textarea.selectionStart ?? textarea.value.length;
      const end = textarea.selectionEnd ?? start;
      const before = textarea.value.slice(0, start);
      const after = textarea.value.slice(end);
      const needsSpace = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n');
      const insert = (needsSpace ? ' ' : '') + tag + ' ';
      const newValue = before + insert + after;

      const nativeSet =
        Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set ??
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      nativeSet?.call(textarea, newValue);
      textarea.dispatchEvent(new Event('input', { bubbles: true }));

      const cursorPos = start + insert.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
      textarea.focus();
    },
    [],
  );

  return (
    <DebugLogProvider threadId={currentThreadId} getToken={getToken}>
    <div className="flex h-[calc(100vh-0px)]">
      {/* Sidebar */}
      <div className="w-[260px] shrink-0">
        <ThreadListSidebar />
      </div>

      {/* Main content area */}
      <div ref={chatColumnRef} className="flex-1 flex flex-col min-w-0 bg-white">
        {/* Header */}
        <div className="h-12 px-4 flex items-center border-b border-zinc-200 bg-white shrink-0">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0">
                {editingTitle ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveTitle();
                        if (e.key === 'Escape') cancelEditTitle();
                      }}
                      onBlur={saveTitle}
                      placeholder="Thread title"
                      className="text-sm font-medium text-zinc-900 bg-white border border-zinc-300 rounded-md px-2 py-0.5 min-w-[240px] focus:outline-none focus:ring-2 focus:ring-zinc-900/5"
                    />
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={saveTitle}
                      className="p-1 rounded-md text-zinc-600 hover:bg-zinc-100 transition-colors"
                      title="Save title"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={cancelEditTitle}
                      className="p-1 rounded-md text-zinc-400 hover:bg-zinc-100 transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group/title flex items-center gap-1.5">
                    <h1 className="text-sm font-medium text-zinc-900 truncate max-w-[420px]">
                      {threadTitle ?? 'New Chat'}
                    </h1>
                    {currentThreadId && (
                      <button
                        onClick={startEditTitle}
                        className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 opacity-0 group-hover/title:opacity-100 focus:opacity-100 transition-opacity"
                        title="Rename thread"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                onClick={toggleWorkspace}
                className={`flex items-center gap-1.5 h-7 px-2 rounded-md text-xs transition-colors ${
                  workspaceOpen
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'
                }`}
                title="Toggle workspace files"
              >
                <FolderOpen className="w-3.5 h-3.5" />
                Workspace
              </button>
              <button
                onClick={toggleDebug}
                className={`flex items-center gap-1.5 h-7 px-2 rounded-md text-xs transition-colors ${
                  debugOpen
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'
                }`}
                title="Toggle debug console"
              >
                <Bug className="w-3.5 h-3.5" />
                Debug
              </button>
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0">
            <div className="h-full max-w-3xl mx-auto">
              <AiChatRuntime>
                <AiChat className="h-full" />
              </AiChatRuntime>
            </div>
          </div>
          {debugOpen && (
            <div className="h-[280px] shrink-0 border-t border-gray-800">
              <DebugPanel onClose={closeDebug} />
            </div>
          )}
        </div>
      </div>

      {/* Right Panel (workspace) */}
      {workspaceOpen && (
        <div className="w-[320px] shrink-0">
          {currentThreadId && showEditor ? (
            <SandboxEditorPanel
              key={currentThreadId}
              threadId={currentThreadId}
              onClose={closeWorkspace}
              onUnavailable={markEditorUnavailable}
              chatColumnRef={chatColumnRef}
              onUseInChat={handleUseInChat}
              onNewThreadWithWorkspace={handleNewThreadWithWorkspace}
              newThreadBusy={newThreadBusy}
            />
          ) : currentThreadId ? (
            <WorkspacePanel threadId={currentThreadId} onClose={closeWorkspace} getToken={getToken} onUseInChat={handleUseInChat} onNewThreadWithWorkspace={handleNewThreadWithWorkspace} newThreadBusy={newThreadBusy} />
          ) : (
            <div className="flex flex-col h-full bg-white border-l border-zinc-200">
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-200 bg-zinc-50/80 shrink-0">
                <div className="flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-zinc-600 shrink-0" />
                  <span className="text-xs font-semibold text-zinc-700">Workspace</span>
                </div>
                <button onClick={closeWorkspace} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors">
                  <span className="sr-only">Close</span>
                  <svg className="w-3.5 h-3.5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
                  <span className="text-xs text-zinc-400">Preparing workspace...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </DebugLogProvider>
  );
}
